import { cx } from '@linaria/core'
import type { FC, ReactNode } from 'react'

import { CosimaElement } from './CosimaViewComponent'
import type { PkBenefits, PkDescription, PkFunctions, PkShortDescription, PkTechData } from './document'
import type { EnhancedCosimaRoot } from './util'

const Headline: FC<{ root: EnhancedCosimaRoot; className?: string }> = ({
  root: {
    details: { editionDetails, meta },
  },
  className,
}) => {
  return (
    <div className={cx('_cosima-headline', className)}>
      <div className="_cosima-code">{editionDetails.code}</div>
      <div className="_cosima-name">{meta?.pkCosimaCodeName.content}</div>
    </div>
  )
}

const ShortDescription: FC<{ shortDescription: PkShortDescription | undefined; className?: string }> = ({ shortDescription, className }) => {
  return (
    <div className={cx('_cosima-short-description', className)}>
      {shortDescription?.content.map((x, i) => (
        <CosimaElement key={i} element={x} />
      ))}
    </div>
  )
}

const Benefits: FC<{ benefits: PkBenefits | undefined; className?: string }> = ({ benefits, className }) => {
  if (!benefits) {
    return undefined
  }
  return (
    <div className={cx('_cosima-benefits', className)}>
      <h1>Benefits</h1>
      {benefits.content.map((x, i) => (
        <CosimaElement key={i} element={x} />
      ))}
    </div>
  )
}

const Description: FC<{ description: PkDescription | undefined; className?: string }> = ({ description, className }) => {
  if (!description) {
    return undefined
  }
  return (
    <div className={cx('_cosima-description', className)}>
      <h1>Description</h1>
      {description.block.map((x, i) => (
        <div key={`${i}`} className="_cosima-description-block">
          <div className="_cosima-title">{x.title}</div>
          <div className="_cosima-description">
            {x.content.map((y, j) => (
              <CosimaElement key={`${i}--${j}`} element={y} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

const TechnicalData: FC<{ techdata: PkTechData | undefined; className?: string }> = ({ techdata, className }) => {
  if (!techdata) {
    return undefined
  }
  return (
    <div className={cx('_cosima-technical-data', className)}>
      <h1>Technical Data</h1>
      <CosimaElement element={techdata.table} />
      {techdata.figure.map((x, i) => (
        <CosimaElement key={i} element={x} />
      ))}
    </div>
  )
}

const Functions: FC<{ functions: PkFunctions | undefined; className?: string }> = ({ functions, className }) => {
  if (!functions) {
    return undefined
  }
  return (
    <div className={cx('_cosima-technical-data', className)}>
      <h1>Functions</h1>
      {functions.block.map((x, i) => (
        <div key={`${i}`} className="_cosima-function-block">
          <div className="_cosima-title">{x.title}</div>
          <div className="_cosima-description">
            {x.content.map((y, j) => (
              <CosimaElement key={`${i}--${j}`} element={y} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Renders a cosima document in an order of:
 * ```txt
 * _cosima-document
 *   > _cosima-meta-figure > {CosimaViewComponent}
 *   > _cosima-headline
 *     > _cosima-code
 *     > _cosima-name
 *   > _cosima-short-description > {CosimaViewComponent}
 *   > _cosima-benefits > {CosimaViewComponent}
 *   > _cosima-description
 *     > _cosima-description-block
 *       > _cosima-title
 *       > _cosima-description > {CosimaViewComponent}
 *   > _cosima-technical-data > {CosimaViewComponent}
 *
 * ```
 * @see {@link CosimaElement}
 */

export const CosimaDocument: FC<{ root?: EnhancedCosimaRoot; className?: string }> = ({ root, className }) => {
  if (!root) {
    return undefined
  }

  const { meta } = root.details

  return (
    <div className={cx('_cosima-document', className)}>
      <CosimaElement element={meta?.figure} className="_cosima-meta-figure" />
      <Headline root={root} className={className} />
      <ShortDescription shortDescription={root.details.shortDescription} className={className} />
      <Benefits benefits={root.details.benefits} className={className} />
      <Description description={root.details.description} className={className} />
      <TechnicalData techdata={root.details.techdata} className={className} />
      <Functions functions={root.details.functions} className={className} />
    </div>
  )
}

/**
 * A component to display the most common top-level components of a cosima document
 */
export const CosimaViewComponent: FC<{
  details: PkShortDescription | PkBenefits | PkDescription | PkTechData | PkFunctions | string | undefined
  fallback?: ReactNode
  className?: string
}> = ({ details, fallback, className }) => {
  if (typeof details === 'string') {
    return <div className={cx('_description', className)}>{details}</div>
  }
  switch (details?.type) {
    case 'PK-short-description':
      return <ShortDescription shortDescription={details} className={className} />
    case 'PK-benefits':
      return <Benefits benefits={details} className={className} />
    case 'PK-description':
      return <Description description={details} className={className} />
    case 'PK-techdata':
      return <TechnicalData techdata={details} className={className} />
    case 'PK-functions':
      return <Functions functions={details} className={className} />
    case undefined:
      return fallback
  }
}
